import * as React from "react";
import { Alert, Button, Card, Modal, Popconfirm, Space, Tabs } from "antd";
import { CloseOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Table } from "../../newcomponents/table/table";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { TableModal } from "../../components/antd/TableModal";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { User } from "../../schema/types/user/entity";
import { ServiceAccount } from "../../schema/types/serviceAccount/entity";
import { Group } from "../../schema/types/group/entity";
import { Identity } from "../../schema/types/identity/entity";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { UserNameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/userNameDescriptionColumn";
import { OriginColumn } from "../../newcomponents/table/columns/wellKnown/originColumn";
import { GvcColumn } from "../../newcomponents/table/columns/wellKnown/gvcColumn";
import { getPrincipalLines } from "./utils";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { BindingInput, PolicyInput } from "../../schema/types/policy/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { getUserTableItem, UserTableItem } from "../../mst/kinds/user.helpers";
import { getGroupTableItem, GroupTableItem } from "../../mst/kinds/group.helpers";
import { getIdentityTableItem, IdentityTableItem } from "../../mst/kinds/identity.helpers";
import { getServiceAccountTableItem, ServiceAccountTableItem } from "../../mst/kinds/serviceAccount.helpers";
import { PermissionWithImplied } from "../../schema/types/policy/entity";
import { ArrayString } from "../../schema/types/org/input";
import { v4 as uuidv4 } from "uuid";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";

interface Props {}
export const Bindings: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const [isAddingBinding, setIsAddingBinding] = React.useState<number | boolean>(false);

  const targetKind = useCpFormValue(`targetKind`);

  const { kind: userTableKind, id: userTableId } = useTableKindId("user", "policy-binding-user");
  const qDataUser = useTableItemQueryData<User>(userTableKind, {});
  const { kind: groupTableKind, id: groupTableId } = useTableKindId("group", "policy-binding-group");
  const qDataGroup = useTableItemQueryData<Group>(groupTableKind, {});
  const { kind: identityTableKind, id: identityTableId } = useTableKindId("identity", "policy-binding-identity");
  const qDataIdentity = useTableItemQueryData<Identity>(identityTableKind, {});
  const { kind: serviceaccountTableKind, id: serviceaccountTableId } = useTableKindId(
    "serviceaccount",
    "policy-binding-serviceaccount",
  );
  const qDataServiceaccount = useTableItemQueryData<ServiceAccount>(serviceaccountTableKind, {});

  const bindings = useCpFormValue(`bindings`);
  const filteredBindings = React.useMemo(() => {
    return [...bindings].filter((_, index) => {
      if (isAddingBinding !== true && isAddingBinding !== false) {
        return index !== isAddingBinding;
      }
      return true;
    });
  }, [isAddingBinding, bindings]);

  const allPermissionSets = React.useMemo(() => filteredBindings.map((r) => r.permissions), [filteredBindings]);
  const permissions = useCpFormValue(`meta.permissionsWithImplied`) || {};
  const [permissionSelection, setPermissionSelection] = React.useState<string[]>([]);

  const [userLinkSelection, setUserLinkSelection] = React.useState<string[]>([]);
  const [userItemSelection, setUserItemSelection] = React.useState<UserTableItem[]>([]);
  const [groupLinkSelection, setGroupLinkSelection] = React.useState<string[]>([]);
  const [groupItemSelection, setGroupItemSelection] = React.useState<GroupTableItem[]>([]);
  const [identityLinkSelection, setIdentityLinkSelection] = React.useState<string[]>([]);
  const [identityItemSelection, setIdentityItemSelection] = React.useState<IdentityTableItem[]>([]);
  const [serviceAccountLinkSelection, setServiceaccountLinkSelection] = React.useState<string[]>([]);
  const [serviceAccountItemSelection, setServiceaccountItemSelection] = React.useState<ServiceAccountTableItem[]>([]);

  const [userCache, setUserCache] = React.useState<Record<string, UserTableItem>>({});
  const [groupCache, setGroupCache] = React.useState<Record<string, GroupTableItem>>({});
  const [identityCache, setIdentityCache] = React.useState<Record<string, IdentityTableItem>>({});
  const [serviceAccountCache, setServiceaccountCache] = React.useState<Record<string, ServiceAccountTableItem>>({});

  const userTableItems = qDataUser.visibleItems.map((i) => getUserTableItem({ item: i }));
  const groupTableItems = qDataGroup.visibleItems.map((i) => getGroupTableItem({ item: i }));
  const identityTableItems = qDataIdentity.visibleItems.map((i) => getIdentityTableItem({ item: i }));
  const serviceAccountTableItems = qDataServiceaccount.visibleItems.map((i) => getServiceAccountTableItem({ item: i }));

  const onDoneItemForm = async () => {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    try {
      if (permissionSetConflicts) {
        Modal.info({
          title: "Permission Set Conflict",
          content:
            "There is already a binding with the same permission set, change selected permissions to be able to continue.",
        });
        return;
      }

      if (permissionSelection.length < 1) {
        Modal.info({
          title: "Permission Selection Issue",
          content: "Each binding requires at least 1 permission to be selected.",
        });
        return;
      }

      const currentBindings = cloneDeep(values.bindings);

      const binding: BindingInput = {
        permissions: permissionSelection.map((ps) => ({ id: uuidv4(), value: ps })),
        principalLinks: [],
      };

      for (const userLink of userLinkSelection) {
        const { name } = parseLink(userLink, { useInputCtx: true });
        const item = userItemSelection.find((i) => i?.selfLink === userLink);
        if (item) {
          userCache[userLink] = item;
        }
        const data: KindSelectState = {
          value: name,
          link: userLink,
          item: item,
        };
        binding.principalLinks.push(data);
      }
      for (const groupLink of groupLinkSelection) {
        const { name } = parseLink(groupLink, { useInputCtx: true });
        const item = groupItemSelection.find((i) => i?.selfLink === groupLink);
        if (item) {
          groupCache[groupLink] = item;
        }
        const data: KindSelectState = {
          value: name,
          link: groupLink,
          item: item,
        };
        binding.principalLinks.push(data);
      }
      for (const identityLink of identityLinkSelection) {
        const { name, gvcRelative } = parseLink(identityLink, { useInputCtx: true });
        const item = identityItemSelection.find((i) => i?.selfLink === identityLink);
        if (item) {
          identityCache[identityLink] = item;
        }
        const data: KindSelectState = {
          value: gvcRelative,
          link: identityLink,
          item: item,
        };
        binding.principalLinks.push(data);
      }
      for (const serviceAccountLink of serviceAccountLinkSelection) {
        const { name } = parseLink(serviceAccountLink, { useInputCtx: true });
        const item = serviceAccountItemSelection.find((i) => i?.selfLink === serviceAccountLink);
        if (item) {
          serviceAccountCache[serviceAccountLink] = item;
        }
        const data: KindSelectState = {
          value: name,
          link: serviceAccountLink,
          item: item,
        };
        binding.principalLinks.push(data);
      }

      if (binding.principalLinks.length < 1) {
        Modal.info({
          title: "Principal Link Issue",
          content: "Each binding requires at least 1 principal to be selected.",
        });
        return;
      }

      if (typeof isAddingBinding !== "boolean") {
        currentBindings.splice(isAddingBinding, 1, binding);
      } else {
        currentBindings.push(binding);
      }
      formSetValue(id, `bindings`, currentBindings);

      setUserCache(userCache);
      setGroupCache(groupCache);
      setIdentityCache(identityCache);
      setServiceaccountCache(serviceAccountCache);
      setPermissionSelection([]);
      setUserLinkSelection([]);
      setUserItemSelection([]);
      setGroupLinkSelection([]);
      setGroupItemSelection([]);
      setIdentityLinkSelection([]);
      setIdentityItemSelection([]);
      setServiceaccountLinkSelection([]);
      setServiceaccountItemSelection([]);
      setIsAddingBinding(false);

      formTriggerValidation(id);
    } catch (e) {
      console.log(`throw`, e.message);
    }
  };

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    const currentBindings = cloneDeep(values.bindings);
    currentBindings.splice(index, 1);
    formSetValue(id, `bindings`, currentBindings);

    formTriggerValidation(id);
  };

  const permissionSetConflicts = allPermissionSets.some((ps) => {
    if (permissionSelection.length !== ps.length) {
      return false;
    }
    for (const permission of permissionSelection) {
      if (!ps.some((ps) => ps.value.includes(permission))) {
        return false;
      }
    }
    return true;
  });

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Bindings</CardTitle>
          <CardContent>
            <Table<BindingInput>
              tableId="policy-bindings-editable"
              data={bindings}
              hideSettings
              columns={[
                {
                  id: "permissions",
                  label: "Permissions",
                  enableResize: true,
                  size: 200,
                  cell: (p) => {
                    const permissionCount = p.row.original.permissions.length;
                    const principalLines = getPrincipalLines(p.row.original.principalLinks.map((pl) => pl.link));
                    const principalCount = principalLines.length;
                    const count = Math.max(permissionCount, principalCount);
                    const arr = new Array(count).fill("");
                    const values: ArrayString[] = p.getValue();

                    return (
                      <div className="flex flex-col items-start h-full py-2">
                        {arr.map((_, i) => {
                          const value = values[i]?.value;
                          return (
                            <span key={`${value}-${i}`} style={{ visibility: value ? "visible" : "hidden" }}>
                              {value || "x"}
                            </span>
                          );
                        })}
                      </div>
                    );
                  },
                },
                {
                  id: "principalLinks",
                  label: "Principals",
                  enableResize: true,
                  size: 200,
                  cell: (p) => {
                    const permissionCount = p.row.original.permissions.length;
                    const principalLines = getPrincipalLines(p.row.original.principalLinks.map((pl) => pl.link));
                    const principalCount = principalLines.length;
                    const count = Math.max(permissionCount, principalCount);
                    const arr = new Array(count).fill("");

                    return (
                      <div className="flex flex-col items-start h-full py-2">
                        {arr.map((_, i) => {
                          const principalLine = principalLines[i];
                          if (!principalLine) {
                            return (
                              <span key={`x-${i}`} style={{ visibility: "hidden" }}>
                                {"x"}
                              </span>
                            );
                          }
                          if (principalLine.isLabel) {
                            return (
                              <span key={`${principalLine.content}`} className="font-semibold">
                                {principalLine.content}
                              </span>
                            );
                          }
                          if (principalLine.link) {
                            return (
                              <InternalLink
                                key={`${principalLine.content}-internallink`}
                                label={principalLine.content}
                                to={`/console${principalLine.link}`}
                                forceTargetBlank={true}
                              />
                            );
                          }
                          return <span key={`${principalLine.content}-fallback`}>{principalLine.content}</span>;
                        })}
                      </div>
                    );
                  },
                },
                {
                  id: "action",
                  label: "",
                  enableResize: false,
                  size: 120,
                  cell: ({ row }) => {
                    return (
                      <div className="w-full flex justify-end">
                        <Space.Compact>
                          <Button
                            htmlType={`button`}
                            onClick={() => {
                              setIsAddingBinding(row.index);
                              setPermissionSelection(row.original.permissions.map((p) => p.value));
                              setUserLinkSelection(
                                row.original.principalLinks.filter((l) => l.link.includes("/user/")).map((l) => l.link),
                              );
                              setGroupLinkSelection(
                                row.original.principalLinks
                                  .filter((l) => l.link.includes("/group/"))
                                  .map((l) => l.link),
                              );
                              setIdentityLinkSelection(
                                row.original.principalLinks
                                  .filter((l) => l.link.includes("/identity/"))
                                  .map((l) => l.link),
                              );
                              setServiceaccountLinkSelection(
                                row.original.principalLinks
                                  .filter((l) => l.link.includes("/serviceaccount/"))
                                  .map((l) => l.link),
                              );
                            }}
                            type={"text"}
                            icon={<EditOutlined />}
                          />
                          <Popconfirm title={"Confirm"} onConfirm={() => onRemoveItem(row.index)}>
                            <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                          </Popconfirm>
                        </Space.Compact>
                      </div>
                    );
                  },
                },
              ]}
            />
            <TableModal
              open={isAddingBinding !== false}
              onCancel={() => setIsAddingBinding(false)}
              onOk={() => {
                onDoneItemForm();
              }}
            >
              <Tabs
                items={[
                  {
                    key: "permission",
                    label: "Permissions",
                    children: (
                      <Table<PermissionWithImplied>
                        tableId={`policy-binding-permission`}
                        selectMode="multi"
                        selectKey="name"
                        selections={permissionSelection}
                        onSelectionsChange={(keys) => {
                          setPermissionSelection(keys);
                        }}
                        data={permissions[targetKind] || []}
                        hideSettings
                        columns={[
                          { label: "Name", id: "name" },
                          { label: "Implied", id: "implied", cell: (p) => p.getValue().join(", ") },
                          { label: "Description", id: "description" },
                        ]}
                      />
                    ),
                  },
                  {
                    key: "user",
                    label: "Users",
                    children: (
                      <div
                        style={{
                          //
                          height: "54vh",
                          overflowY: "auto",
                        }}
                      >
                        <Table<UserTableItem>
                          tableId={userTableId}
                          selectMode="multi"
                          selectKey="selfLink"
                          selections={userLinkSelection}
                          onSelectionsChange={(keys, items) => {
                            setUserLinkSelection(keys);
                            setUserItemSelection(items!);
                          }}
                          data={userTableItems}
                          headerRenderer={() => {
                            return (
                              <>
                                <TableHeaderRefreshButton onClick={qDataUser.fetchItems} />
                                <CustomTableCPLNQuery
                                  tableId={userTableId}
                                  kind={userTableKind}
                                  onQuery={qDataUser.setQuery}
                                  isLoading={qDataUser.isLoadingQueried}
                                />
                              </>
                            );
                          }}
                          enableFilter
                          isLoading={qDataUser.isLoading}
                          onSortChange={qDataUser.setSort}
                          onSearchChange={qDataUser.setSearch}
                          isLoadingMore={qDataUser.isLoadingMore}
                          hasMore={qDataUser.hasMore}
                          onLoadMore={qDataUser.fetchNextPage}
                          columns={[
                            UserNameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }), //
                            TagsColumn(),
                            CreatedColumn(),
                            LastModifiedColumn(),
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "group",
                    label: "Groups",
                    children: (
                      <div
                        style={{
                          //
                          height: "54vh",
                          overflowY: "auto",
                        }}
                      >
                        <Table<GroupTableItem>
                          tableId={groupTableId}
                          selectMode="multi"
                          selectKey="selfLink"
                          selections={groupLinkSelection}
                          onSelectionsChange={(keys, items) => {
                            setGroupLinkSelection(keys);
                            setGroupItemSelection(items!);
                          }}
                          data={groupTableItems}
                          headerRenderer={() => {
                            return (
                              <>
                                <TableHeaderRefreshButton onClick={qDataGroup.fetchItems} />
                                <CustomTableCPLNQuery
                                  tableId={groupTableId}
                                  kind={groupTableKind}
                                  onQuery={qDataGroup.setQuery}
                                  isLoading={qDataGroup.isLoadingQueried}
                                />
                              </>
                            );
                          }}
                          enableFilter
                          isLoading={qDataGroup.isLoading}
                          onSortChange={qDataGroup.setSort}
                          onSearchChange={qDataGroup.setSearch}
                          isLoadingMore={qDataGroup.isLoadingMore}
                          hasMore={qDataGroup.hasMore}
                          onLoadMore={qDataGroup.fetchNextPage}
                          columns={[
                            NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }), //
                            OriginColumn(),
                            TagsColumn(),
                            CreatedColumn(),
                            LastModifiedColumn(),
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "identity",
                    label: "Identities",
                    children: (
                      <div
                        style={{
                          //
                          height: "54vh",
                          overflowY: "auto",
                        }}
                      >
                        <Table<IdentityTableItem>
                          tableId={identityTableId}
                          selectMode="multi"
                          selectKey="selfLink"
                          selections={identityLinkSelection}
                          onSelectionsChange={(keys, items) => {
                            setIdentityLinkSelection(keys);
                            setIdentityItemSelection(items!);
                          }}
                          data={identityTableItems}
                          headerRenderer={() => {
                            return (
                              <>
                                <TableHeaderRefreshButton onClick={qDataIdentity.fetchItems} />
                                <CustomTableCPLNQuery
                                  tableId={identityTableId}
                                  kind={identityTableKind}
                                  onQuery={qDataIdentity.setQuery}
                                  isLoading={qDataIdentity.isLoadingQueried}
                                />
                              </>
                            );
                          }}
                          enableFilter
                          isLoading={qDataIdentity.isLoading}
                          onSortChange={qDataIdentity.setSort}
                          onSearchChange={qDataIdentity.setSearch}
                          isLoadingMore={qDataIdentity.isLoadingMore}
                          hasMore={qDataIdentity.hasMore}
                          onLoadMore={qDataIdentity.fetchNextPage}
                          columns={[
                            NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }), //
                            GvcColumn(),
                            TagsColumn(),
                            CreatedColumn(),
                            LastModifiedColumn(),
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "serviceaccount",
                    label: "Service Accounts",
                    children: (
                      <div
                        style={{
                          //
                          height: "54vh",
                          overflowY: "auto",
                        }}
                      >
                        <Table<ServiceAccountTableItem>
                          tableId={serviceaccountTableId}
                          selectMode="multi"
                          selectKey="selfLink"
                          selections={serviceAccountLinkSelection}
                          onSelectionsChange={(keys, items) => {
                            setServiceaccountLinkSelection(keys);
                            setServiceaccountItemSelection(items!);
                          }}
                          data={serviceAccountTableItems}
                          headerRenderer={() => {
                            return (
                              <>
                                <TableHeaderRefreshButton onClick={qDataServiceaccount.fetchItems} />
                                <CustomTableCPLNQuery
                                  tableId={serviceaccountTableId}
                                  kind={serviceaccountTableKind}
                                  onQuery={qDataServiceaccount.setQuery}
                                  isLoading={qDataServiceaccount.isLoadingQueried}
                                />
                              </>
                            );
                          }}
                          enableFilter
                          isLoading={qDataServiceaccount.isLoading}
                          onSortChange={qDataServiceaccount.setSort}
                          onSearchChange={qDataServiceaccount.setSearch}
                          isLoadingMore={qDataServiceaccount.isLoadingMore}
                          hasMore={qDataServiceaccount.hasMore}
                          onLoadMore={qDataServiceaccount.fetchNextPage}
                          columns={[
                            NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }), //
                            OriginColumn(),
                            TagsColumn(),
                            CreatedColumn(),
                            LastModifiedColumn(),
                          ]}
                        />
                      </div>
                    ),
                  },
                ]}
              />
              <div>
                {permissionSetConflicts ? (
                  <Alert
                    className="mt-2"
                    type="error"
                    message={"There is already a binding with this permission set."}
                  />
                ) : null}
              </div>
            </TableModal>
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setPermissionSelection([]);
                setUserLinkSelection([]);
                setUserItemSelection([]);
                setGroupLinkSelection([]);
                setGroupItemSelection([]);
                setIdentityLinkSelection([]);
                setIdentityItemSelection([]);
                setServiceaccountLinkSelection([]);
                setServiceaccountItemSelection([]);
                setIsAddingBinding(true);
              }}
            >
              Add Binding
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
